import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, finalize, map, switchMap, tap, withLatestFrom } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import { betslipTypeStateSelector } from '../../types/selectors';
import { EditBetActions } from '../actions';
import { EditBetEarlyPayoutSubscriptionService } from '../services/edit-bet-early-payout-subscription.service';
import { EditBetEarlyPayoutSubscriptionServiceFactory } from '../services/edit-bet-early-payout-subscription.service.factory';

@Injectable()
export class BetslipEditBetEarlyPayoutSubscriptionEffects {
    private subscriptionService: EditBetEarlyPayoutSubscriptionService;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private subscriptionServiceFactory: EditBetEarlyPayoutSubscriptionServiceFactory,
    ) {}

    /**
     * Handle start edit bet.
     * Subscribe to early payout update. Apply updates only during edit phase
     */
    handleEarlyPayoutSubscriptionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EditBetActions.init),
            tap((action) => {
                if (this.subscriptionService) {
                    this.subscriptionService.destroy();
                }
                this.subscriptionService = this.subscriptionServiceFactory.createAndSubscribe(action.payload.sourceBetslip);
            }),
            withLatestFrom(this.store.select(betslipSelector)),
            switchMap(([action, betslipState]) =>
                this.subscriptionService.earlyPayout$.pipe(
                    map((e) => {
                        return EditBetActions.earlyPayoutUpdated({ payload: e });
                    }),
                    finalize(() => {
                        this.subscriptionService.unsubscribe();
                    }),
                ),
            ),
        );
    });

    /**
     * When start placing unsubscribe from eps push.
     */
    handleStartPlaceBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.startedPlaceBet),
            withLatestFrom(this.store.select(betslipTypeStateSelector)),
            filter(([action, typeState]) => typeState.base.currentSelectedType === BetslipType.EditBet),
            map(() => EditBetActions.unsubscribeFromEarlyPayout()),
        );
    });

    /**
     * Handle place bet failed for edit bet.
     * Then we need to update early payout with the latest value we have.
     */
    handlePlaceBetFailedPlaceBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPlaceBetActions.placeBetFailed),
            withLatestFrom(this.store.select(betslipTypeStateSelector)),
            filter(([action, typeState]) => typeState.base.currentSelectedType === BetslipType.EditBet),
            map(() => EditBetActions.subscribeToEarlyPayout()),
        );
    });

    /**
     * Handle exit edit bet.
     * Unsubscribe from early payout updates.
     */
    handleExitEditBetEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(EditBetActions.exitEditBet),
                tap(() => {
                    if (this.subscriptionService) {
                        this.subscriptionService.destroy();
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleSubscribeToEarlyPayoutEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(EditBetActions.subscribeToEarlyPayout),
                tap(() => {
                    if (this.subscriptionService) {
                        if (this.subscriptionService.isSubscribed) {
                            this.subscriptionService.unsubscribe();
                        }
                        this.subscriptionService.subscribe();
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleUnsubscribeFromEarlyPayoutEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(EditBetActions.unsubscribeFromEarlyPayout),
                tap(() => {
                    if (this.subscriptionService) {
                        this.subscriptionService.unsubscribe();
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
